import * as echarts from "echarts/core";
import { EChartsType } from "echarts/core";

import { getPerformanceOverlayLayout } from "./PerformanceInfoOverlayLayout";
import { PerformanceInfoOverlayItem } from "./PerformanceInfoOverlay";

const EXPORT_CONTAINER_LEFT = "-10000px";
const OVERLAY_FONT_SIZE = 11;
const OVERLAY_ROW_HEIGHT = 17;
const OVERLAY_COLUMN_GAP = 6;

const renderOverlayValue = (item: PerformanceInfoOverlayItem) => {
  if (item.value === null || item.value === undefined || item.value === "") {
    return "-";
  }

  return `${item.value}${item.unit ? ` ${item.unit}` : ""}`;
};

const estimateTextWidth = (text: string) => {
  // Export-only approximation; it mirrors the DOM overlay closely enough without
  // involving browser layout in the ECharts canvas export path.
  return Math.ceil(text.length * OVERLAY_FONT_SIZE * 0.62);
};

const buildOverlayExportSeries = (
  name: string,
  items: PerformanceInfoOverlayItem[],
  placement: "top" | "bottom"
) => {
  if (items.length === 0) {
    return null;
  }

  return {
    name,
    type: "custom",
    silent: true,
    data: [0],
    z: 1000,
    renderItem: (_params: any, api: any) => {
      const width = api.getWidth();
      const height = api.getHeight();
      const { topPanelStyle, bottomPanelStyle } = getPerformanceOverlayLayout(width, height);
      const panelStyle = placement === "top" ? topPanelStyle : bottomPanelStyle;
      const right = Number(panelStyle.right) || 0;
      const top = Number(panelStyle.top) || 0;

      const labelWidth = Math.max(
        ...items.map((item) => estimateTextWidth(`${item.label}:`)),
        0
      );
      const valueWidth = Math.max(
        ...items.map((item) => estimateTextWidth(renderOverlayValue(item))),
        0
      );
      const panelWidth = labelWidth + OVERLAY_COLUMN_GAP + valueWidth;
      const x = Math.max(0, width - right - panelWidth);

      const children = items.flatMap((item, index) => {
        const y = top + index * OVERLAY_ROW_HEIGHT;

        return [
          {
            type: "text",
            silent: true,
            style: {
              text: `${item.label}:`,
              x: x + labelWidth,
              y,
              fill: "#000",
              fontSize: OVERLAY_FONT_SIZE,
              fontWeight: 400,
              textAlign: "right",
              textVerticalAlign: "top",
            },
          },
          {
            type: "text",
            silent: true,
            style: {
              text: renderOverlayValue(item),
              x: x + labelWidth + OVERLAY_COLUMN_GAP,
              y,
              fill: "#000",
              fontSize: OVERLAY_FONT_SIZE,
              fontWeight: 400,
              textAlign: "left",
              textVerticalAlign: "top",
            },
          },
        ];
      });

      return {
        type: "group",
        silent: true,
        children,
      };
    },
  };
};

export const exportPerformanceChartImage = (
  sourceInstance: EChartsType,
  exportOptions: Record<string, any>,
  topItems: PerformanceInfoOverlayItem[] = [],
  bottomItems: PerformanceInfoOverlayItem[] = []
) => {
  const sourceDom = sourceInstance.getDom();
  const width = Math.round(sourceDom.clientWidth || 500);
  const height = Math.round(sourceDom.clientHeight || 700);
  const exportContainer = document.createElement("div");
  exportContainer.style.position = "fixed";
  exportContainer.style.left = EXPORT_CONTAINER_LEFT;
  exportContainer.style.top = "0";
  exportContainer.style.width = `${width}px`;
  exportContainer.style.height = `${height}px`;
  exportContainer.style.pointerEvents = "none";
  document.body.appendChild(exportContainer);

  try {
    const exportInstance = echarts.init(exportContainer, undefined, {
      renderer: "canvas",
      width,
      height,
    });

    try {
      const sourceOption = sourceInstance.getOption() as any;
      const sourceSeries = Array.isArray(sourceOption.series) ? sourceOption.series : [];
      const overlaySeries = [
        buildOverlayExportSeries("export top info", topItems, "top"),
        buildOverlayExportSeries("export bottom info", bottomItems, "bottom"),
      ].filter(Boolean);

      exportInstance.setOption(
        {
          ...sourceOption,
          series: [...sourceSeries, ...overlaySeries],
        },
        true
      );

      return exportInstance.getDataURL(exportOptions as any);
    } finally {
      exportInstance.dispose();
    }
  } finally {
    exportContainer.remove();
  }
};

export const downloadPerformanceChartImage = (
  sourceInstance: EChartsType,
  filename: string,
  topItems: PerformanceInfoOverlayItem[] = [],
  bottomItems: PerformanceInfoOverlayItem[] = []
) => {
  const dataUrl = exportPerformanceChartImage(
    sourceInstance,
    {
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#fff",
      excludeComponents: ["toolbox"],
    },
    topItems,
    bottomItems
  );
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = `${filename || "performance-chart"}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
};
